/**
 * Vincent B667 (Eric 2026-08-05 SPEC STRICK PROD) — Hook réutilisable pour
 * polling avec 4 garde-fous cumulés :
 *
 *   1. Verrou anti-empilement (`inFlight`) : skip si le tick précédent
 *      n'est pas encore terminé (évite les cascades quand le backend est lent).
 *   2. Pause quand l'onglet est en background (`document.hidden`).
 *   3. Pause quand un upload chunké est actif (`uploadState.isActive()`) —
 *      évite de saturer le pod origine pendant l'upload (voir B666).
 *   4. Progressive backoff sur erreurs : chaque échec double le délai,
 *      cap à 6× le délai de base. Reset à 1 sur succès.
 *
 * Combiné avec le refresh automatique au retour du foreground (via
 * `visibilitychange`), ce hook élimine les cascades HTTP 520 lors des
 * pics de charge production.
 *
 * @example
 * usePausableInterval(fetchAgoraUnread, 15000);
 * // → poll toutes les 15s par défaut, jusqu'à 90s en cas de pannes.
 *
 * @param fetchFn Async function returning true (succès) or false (échec).
 *                Peut retourner une Promise; le hook attend sa résolution.
 * @param baseIntervalMs Intervalle de polling en ms (par défaut 30 000).
 * @param options.enabled Si false, n'active pas le polling (default true).
 * @param options.deps Dépendances React pour re-créer le poller.
 */
import { useEffect, useRef } from "react";
import { uploadState } from "../lib/uploadState";

export function usePausableInterval(fetchFn, baseIntervalMs = 30000, options = {}) {
  const { enabled = true, deps = [] } = options;
  const inFlightRef = useRef(false);
  const failRef = useRef(0);
  const fetchFnRef = useRef(fetchFn);
  // Toujours utiliser la dernière version du callback sans re-scheduler.
  fetchFnRef.current = fetchFn;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer = null;

    const nextDelay = () => {
      const mult = failRef.current <= 0 ? 1 : Math.min(1 + failRef.current, 6);
      return baseIntervalMs * mult;
    };

    const runTick = async () => {
      if (cancelled) return;
      // Garde-fous : 3 raisons de skipper sans compter l'échec.
      if (typeof document !== "undefined" && document.hidden) {
        // Rescheduler sans exécuter.
        timer = setTimeout(runTick, nextDelay());
        return;
      }
      if (uploadState.isActive()) {
        timer = setTimeout(runTick, nextDelay());
        return;
      }
      if (inFlightRef.current) {
        timer = setTimeout(runTick, nextDelay());
        return;
      }
      inFlightRef.current = true;
      let success = true;
      try {
        const result = await fetchFnRef.current();
        // Convention : undefined/true = succès, false = échec.
        success = result !== false;
      } catch {
        success = false;
      } finally {
        inFlightRef.current = false;
      }
      if (success) {
        failRef.current = 0;
      } else {
        failRef.current += 1;
      }
      if (!cancelled) timer = setTimeout(runTick, nextDelay());
    };

    // Premier tick immédiat.
    runTick();

    // Refresh immédiat au retour du foreground (compat B661).
    const onVisibility = () => {
      if (typeof document !== "undefined" && !document.hidden && !uploadState.isActive()) {
        // Annule le timer courant et relance sans attendre.
        if (timer) clearTimeout(timer);
        runTick();
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, baseIntervalMs, ...deps]);
}
